import { useEffect, useState } from 'react';

const DEFAULT_CTA_SELECTOR = '[data-cta-area]';

interface UseCTAHeightOptions {
  selector?: string;
  enabled?: boolean;
}

export function useCTAHeight({
  selector = DEFAULT_CTA_SELECTOR,
  enabled = true,
}: UseCTAHeightOptions = {}) {
  const [ctaHeight, setCTAHeight] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setCTAHeight(0);
      return;
    }

    if (!window.ResizeObserver) {
      console.warn('ResizeObserver is not supported in this browser');
      return;
    }

    const findAndObserveCTA = () => {
      const ctaElement = document.querySelector(selector) as HTMLElement;

      if (!ctaElement) {
        setCTAHeight(0);
        return null;
      }

      const initialHeight = ctaElement.offsetHeight;
      setCTAHeight(initialHeight);

      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          let height = 0;

          if (entry.borderBoxSize) {
            const borderBoxSize = Array.isArray(entry.borderBoxSize)
              ? entry.borderBoxSize[0]
              : entry.borderBoxSize;
            height = borderBoxSize.blockSize;
          } else {
            height = entry.contentRect.height;
          }

          setCTAHeight(Math.round(height));
        }
      });

      resizeObserver.observe(ctaElement);

      return resizeObserver;
    };

    let observer = findAndObserveCTA();

    const mutationObserver = new MutationObserver(() => {
      const ctaElement = document.querySelector(selector);

      if (ctaElement && !observer) {
        observer = findAndObserveCTA();
      } else if (!ctaElement && observer) {
        observer.disconnect();
        observer = null;
        setCTAHeight(0);
      }
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      if (observer) {
        observer.disconnect();
      }
      mutationObserver.disconnect();
    };
  }, [selector, enabled]);

  return ctaHeight;
}

// 여러 CTA 영역을 동시에 감지하는 훅
export function useMultipleCTAHeight(selectors: string[] = ['[data-cta-area]']) {
  const [totalHeight, setTotalHeight] = useState(0);

  useEffect(() => {
    if (!window.ResizeObserver) {
      console.warn('ResizeObserver is not supported in this browser');
      return;
    }

    const observers: ResizeObserver[] = [];
    const heights = new Map<string, number>();

    const updateTotalHeight = () => {
      const total = Array.from(heights.values()).reduce((sum, height) => sum + height, 0);
      setTotalHeight(total);
    };

    selectors.forEach((selector) => {
      const element = document.querySelector(selector) as HTMLElement;
      if (!element) return;

      // 초기 높이 설정
      heights.set(selector, element.offsetHeight);

      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          let height = 0;

          if (entry.borderBoxSize) {
            const borderBoxSize = Array.isArray(entry.borderBoxSize)
              ? entry.borderBoxSize[0]
              : entry.borderBoxSize;
            height = borderBoxSize.blockSize;
          } else {
            height = entry.contentRect.height;
          }

          heights.set(selector, Math.round(height));
          updateTotalHeight();
        }
      });

      observer.observe(element);
      observers.push(observer);
    });

    // 초기 총 높이 계산
    updateTotalHeight();

    return () => {
      observers.forEach((observer) => {
        observer.disconnect();
      });
    };
  }, [selectors]);

  return totalHeight;
}
